import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { MessageCircle, Send, Search, Loader2, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MessagesPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.other_user.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/api/messages/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const response = await axios.get(`${API}/api/messages/conversation/${otherUserId}`);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    setSendingMessage(true);
    try {
      const response = await axios.post(`${API}/api/messages/send`, {
        receiver_id: selectedConversation.other_user.id,
        content: newMessage
      });
      setMessages([...messages, response.data]);
      setNewMessage('');
      
      // Update conversation list
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${API}/api/users/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearching(false);
    }
  };

  const startConversation = (otherUser) => {
    setSelectedConversation({
      other_user: otherUser
    });
    setMessages([]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      <Header />
      
      <div className="content-container pt-20">
        <div className="py-8">
          <h1 className="text-2xl font-bold text-stone-800 mb-6 font-['Outfit']">Messages</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
            {/* Conversations List */}
            <Card className="border-stone-200 rounded-2xl bg-white lg:col-span-1 flex flex-col">
              <CardHeader className="pb-3 border-b border-stone-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    placeholder="Search nurses..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="rounded-xl pl-10"
                    data-testid="search-users-input"
                  />
                </div>
              </CardHeader>
              
              <ScrollArea className="flex-1">
                <CardContent className="p-2">
                  {/* Search Results */}
                  {searchQuery && searchResults.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-stone-500 px-2 py-1">Search Results</p>
                      {searchResults.map(searchUser => (
                        <button
                          key={searchUser.id}
                          onClick={() => startConversation(searchUser)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-stone-50 transition-colors"
                          data-testid={`search-result-${searchUser.id}`}
                        >
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={searchUser.avatar_url} />
                            <AvatarFallback className="bg-[#6B8E7B] text-white text-sm">
                              {getInitials(searchUser.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-left">
                            <p className="font-medium text-stone-800 text-sm">{searchUser.name}</p>
                            <p className="text-xs text-stone-500">{searchUser.specialization || 'Nurse'}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Conversations */}
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-[#6B8E7B]" />
                    </div>
                  ) : conversations.length === 0 && !searchQuery ? (
                    <div className="text-center py-8 text-stone-500">
                      <Users className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                      <p className="text-sm">No conversations yet</p>
                      <p className="text-xs mt-1">Search for nurses to start chatting</p>
                    </div>
                  ) : (
                    !searchQuery && conversations.map(conv => (
                      <button
                        key={conv.id}
                        onClick={() => setSelectedConversation(conv)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                          selectedConversation?.other_user?.id === conv.other_user.id
                            ? 'bg-[#6B8E7B]/10'
                            : 'hover:bg-stone-50'
                        }`}
                        data-testid={`conversation-${conv.id}`}
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={conv.other_user.avatar_url} />
                          <AvatarFallback className="bg-[#6B8E7B] text-white text-sm">
                            {getInitials(conv.other_user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 text-left min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-stone-800 text-sm truncate">{conv.other_user.name}</p>
                            <span className="text-xs text-stone-400">{formatDate(conv.last_message_at)}</span>
                          </div>
                          <p className="text-xs text-stone-500 truncate">{conv.last_message}</p>
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="w-5 h-5 rounded-full bg-[#E27D60] text-white text-xs flex items-center justify-center">
                            {conv.unread_count}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </CardContent>
              </ScrollArea>
            </Card>

            {/* Messages Area */}
            <Card className="border-stone-200 rounded-2xl bg-white lg:col-span-2 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <CardHeader className="pb-3 border-b border-stone-100">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={selectedConversation.other_user.avatar_url} />
                        <AvatarFallback className="bg-[#6B8E7B] text-white text-sm">
                          {getInitials(selectedConversation.other_user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-stone-800">{selectedConversation.other_user.name}</p>
                        <p className="text-sm text-stone-500">{selectedConversation.other_user.specialization || 'Nurse'}</p>
                      </div>
                    </div>
                  </CardHeader>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.length === 0 ? (
                        <div className="text-center py-12 text-stone-500">
                          <MessageCircle className="h-12 w-12 mx-auto mb-3 text-stone-300" />
                          <p>Start the conversation!</p>
                        </div>
                      ) : (
                        messages.map((msg, index) => (
                          <div
                            key={msg.id || index}
                            className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[70%] ${
                                msg.sender_id === user?.id ? 'message-sent' : 'message-received'
                              }`}
                            >
                              <p className="text-sm">{msg.content}</p>
                              <p className={`text-xs mt-1 ${msg.sender_id === user?.id ? 'text-white/70' : 'text-stone-400'}`}>
                                {formatDate(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <div className="p-4 border-t border-stone-100">
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <Input
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="rounded-xl"
                        data-testid="message-input"
                      />
                      <Button
                        type="submit"
                        className="bg-[#6B8E7B] hover:bg-[#5A7868] rounded-full px-4"
                        disabled={sendingMessage || !newMessage.trim()}
                        data-testid="send-message-button"
                      >
                        {sendingMessage ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-stone-500">
                  <div className="text-center">
                    <MessageCircle className="h-16 w-16 mx-auto mb-4 text-stone-300" />
                    <p className="text-lg font-medium">Select a conversation</p>
                    <p className="text-sm">Choose from your existing conversations or search for a nurse</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
